// package imports
import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, FormControlLabel, Snackbar, Switch } from '@mui/material';
import MuiAlert from '@mui/material/Alert';

// local imports
import { default as SavedSnack } from '../AlertSnack';
import { useObservationStore } from '../../stores/observationStore';
import { useUpdateObservationInstance } from '../../hooks/ObservationInstanceHooks';
import { logClientException } from 'appinsights/clientAppInsights';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const defaultErrorMessage =
  'An unexpected error has occured. Please try again.';
const defaultToastAutoHide = 2500;

const FeedbackProvided = ({ saveOnChange }) => {
  const theme = useTheme();
  const [errorMessage, setErrorMessage] = useState(null);
  const [eventState, setEventState] = useState({
    feedback_provided: false,
  });
  const [snackSave, setSnackSave] = useState({
    message: null,
    severity: 'success',
    autoHideDuration: defaultToastAutoHide,
  });

  const { observation, patchObservation } = useObservationStore();

  const afterObservationMutate = (_data) => {
    setSnackSave({
      message: 'Feedback provided updated',
      severity: 'success',
      autoHideDuration: defaultToastAutoHide,
    });
  };

  const handleFeedbackProvidedToggle = (event) => {
    const newFeedbackValue = event.target.checked;

    setEventState((prevState) => ({
      ...prevState,
      feedback_provided: newFeedbackValue,
    }));

    patchObservation([{ name: 'feedback_provided', value: newFeedbackValue }]);

    if (saveOnChange) {
      setTimeout(() => {
        updateObservationMutation.mutate({
          feedback_provided: newFeedbackValue,
        });
      }, 100); // Small delay to ensure state updates
    }
  };

  const afterObservationMutateFail = (error) => {
    logClientException(error);
    if (error instanceof BadRequestError) {
      setErrorMessage(error.formatMessage());
    } else {
      setErrorMessage(defaultErrorMessage);
    }
    updateObservationMutation.reset();
  };

  const updateObservationMutation = useUpdateObservationInstance(
    observation,
    afterObservationMutate,
    afterObservationMutateFail,
  );

  useEffect(() => {
    setEventState({
      ...eventState,
      feedback_provided: observation?.feedback_provided ?? false,
    });
  }, [observation]);

  const handleAlertClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setErrorMessage(null);
  };

  return (
    <Box sx={{ margin: '1rem 0' }}>
      <FormControlLabel
        sx={{
          fontSize: '10px',
          fontWeight: 400,
          lineHeight: '22px',
          color: eventState.feedback_provided
            ? theme.palette.white.main
            : theme.palette.btLightGrey.dark,
          marginTop: '2px',
        }}
        control={
          <Switch
            color="primary"
            checked={eventState.feedback_provided}
            onChange={(e) => {
              handleFeedbackProvidedToggle(e);
            }}
            name="feedback"
          />
        }
        label="Feedback Provided"
      />

      <SavedSnack
        message={snackSave.message}
        openSnack={snackSave.message === null ? false : true}
        onClose={() => {
          setSnackSave({
            message: null,
            severity: 'success',
            autoHideDuration: defaultToastAutoHide,
          });
        }}
        severity={snackSave.severity}
        autoHideDuration={snackSave.autoHideDuration}
      />

      <Snackbar
        open={errorMessage}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleAlertClose}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* <PleaseWait isLoading={isFetchingCount > 0} /> */}
    </Box>
  );
};

export default FeedbackProvided;
